import {
  normalizeCatalogAttachments,
  normalizeThreadSummaryAttachments,
} from "@/entities/thread/normalizeAttachments";
import type { Catalog, ThreadSummary } from "@/entities/thread/types";

export type CatalogResponse = Omit<Catalog, "pagination"> & {
  pagination?: Catalog["pagination"];
};

export function normalizeCatalogResponse(
  response: CatalogResponse,
  requestLimit: number,
): Catalog {
  return normalizeCatalogAttachments({
    ...response,
    pagination: response.pagination ?? {
      page: 1,
      limit: requestLimit,
      hasNextPage: false,
    },
  });
}

/** 無限表示で取得した各ページを、先頭からの順序を保って1つのカタログへ結合する。 */
export function mergeCatalogPages(pages: readonly Catalog[]): Catalog {
  const firstPage = pages[0];
  const lastPage = pages.at(-1);
  if (!firstPage || !lastPage) {
    throw new RangeError("カタログページがありません");
  }

  const seenThreadIds = new Set<string>();
  const threads = pages.flatMap((page) =>
    page.threads.filter((thread) => {
      if (seenThreadIds.has(thread.id)) return false;
      seenThreadIds.add(thread.id);
      return true;
    }),
  );

  return {
    ...firstPage,
    threads,
    pagination: lastPage.pagination,
  };
}

/** 公開カタログの1ページ目にだけ本人のshadow threadを重複なく追加する。 */
export function mergeViewerShadowThreads(
  catalog: Catalog,
  shadowThreads: ThreadSummary[],
): Catalog {
  if (catalog.pagination.page !== 1 || shadowThreads.length === 0) {
    return catalog;
  }

  const seenThreadIds = new Set(catalog.threads.map((thread) => thread.id));
  const uniqueShadowThreads = shadowThreads
    .map(normalizeThreadSummaryAttachments)
    .filter((thread) => {
      if (seenThreadIds.has(thread.id)) return false;
      seenThreadIds.add(thread.id);
      return true;
    });

  if (uniqueShadowThreads.length === 0) return catalog;
  return {
    ...catalog,
    threads: [...catalog.threads, ...uniqueShadowThreads],
  };
}
